import { appConfigStore } from 'Models/AppConfigStore';
import {
	AdditionalBookingOptionEntity,
	BookingEntity,
	CargoCharacteristicEntity,
	CheckInStatusEntity,
} from 'Models/Entities';
import { ToastOptions } from 'react-toastify';
import { getCheckInData } from 'Services/Api/_HumanWritten/CheckInService';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { formatDateMonth } from 'Util/_HumanWritten/DateUtils';
import alertToast from 'Util/ToastifyUtils';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { getStopById } from 'Global/JourneyElements/Utils/getStopById';
import { PlayCheckInAudio } from 'Views/Components/_HumanWritten/AudioPlayer/AudioPlayer';
import { CheckInBookingOverviewDto, ICheckInBookingOverviewDtoDto } from './CheckInEntities/CheckInBookingOverviewDto';
import { FerryTripDto } from './CheckInEntities/FerryTripDto';
import { CapacityTypeForCheckInDto } from './Types/CapacityTypeForCheckInDto';
import { CheckInSorter, sortByFullName } from './Utils/SorterHelpers/CheckInSorters';

// ==================================================== Models ===================================================== //

export interface DisableContinueState {
	continue: boolean;
}

/**
 * True when the trip's check-in has been finalised AND the operator has enabled the
 * lock-on-finalise config. When true, check-in and booking changes are locked, so the UI hides
 * the relevant controls (the backend enforces the same lock). Pure so it stays free of the
 * check-in store's import cycle — pass the current trip.
 */
export function isCheckInLocked(ferryTrip?: FerryTripDto): boolean {
	return !!ferryTrip?.checkInFinalised && appConfigStore.lockChangesWhenCheckInFinalised;
}

export const getCheckInTableHeader = (x: CheckInSorter): CheckInSorter => {
	switch (x) {
		case CheckInSorter.Default:
		case CheckInSorter.Fullname:
		case CheckInSorter.FullnameDesc:
			return CheckInSorter.Fullname;
		case CheckInSorter.Rego:
			return CheckInSorter.Rego;
		case CheckInSorter.VehicleType:
		case CheckInSorter.VehicleTypeDesc:
			return CheckInSorter.VehicleType;
		case CheckInSorter.Trailer:
		case CheckInSorter.TrailerDesc:
			return CheckInSorter.Trailer;
		case CheckInSorter.Vehicle:
		case CheckInSorter.VehicleDesc:
			return CheckInSorter.Vehicle;
		case CheckInSorter.Company:
		case CheckInSorter.CompanyDesc:
			return CheckInSorter.Company;
		default:
			return CheckInSorter.Fullname;
	}
};

// ================================================== GQL Fetches ================================================== //

interface CheckInBookingDto {
	ferryTripDto: FerryTripDto;
	bookingSummaries: CheckInBookingOverviewDto[];
	checkInStatuses: CheckInStatusEntity[];
	capacityTypes: CapacityTypeForCheckInDto[];
	cargoCharacteristics: CargoCharacteristicEntity[];
}

/**
 * Returns ferry trip and booked bookings based on ferry trip id.
 */
export async function fetchCheckInData(ferryTripId?: string): Promise<CheckInBookingDto | null> {
	if (!ferryTripId) {
		return new Promise(resolve => resolve(null));
	}

	const response = await getCheckInData(ferryTripId);

	// No trip in the payload means the fetch failed. Returning null lets the caller surface a load
	// error — building a FerryTripDto from nothing gives a blank-but-truthy trip (no routeId), which
	// passes every "is it loaded?" check and then breaks the check-in page.
	if (!response?.data?.ferryTrip) {
		return null;
	}

	// Process entities as a class to access class functions.
	const ferryTrip = new FerryTripDto(response.data.ferryTrip);
	const bookings = response?.data?.checkInBookingOverviews
		?.filter((x: any) => isNotNullOrUndefined(x))
		?.map((x: Partial<ICheckInBookingOverviewDtoDto> | undefined) => new CheckInBookingOverviewDto(x));
	const filteredbookings = filterBookedBookings(bookings);

	const capacityTypes = response?.data?.capacityTypes.map((x: any) => new CapacityTypeForCheckInDto(x));
	const cargoCharacteristics = response?.data?.cargoCharacteristics.map((x: any) => new CargoCharacteristicEntity(x));

	return {
		ferryTripDto: ferryTrip,
		bookingSummaries: filteredbookings,
		checkInStatuses: response?.data?.checkInStatuses ?? [],
		capacityTypes,
		cargoCharacteristics,
	};
}

/**
 * Process has three steps in order:
 * - Filters bookings by BOOKED
 * - Instantiate class for each
 * - Order list by full name
 * @param bookings This list of bookings may not have instantiated classes.
 */
function filterBookedBookings(bookings: CheckInBookingOverviewDto[]): CheckInBookingOverviewDto[] {
	const bookedBookings = bookings; // [
	// 	...bookings
	// 		.filter(x => x.bookingStatus === 'BOOKED')
	// 		.map(x => new BookingEntity(x)),
	// ];
	bookedBookings.sort(sortByFullName());

	return bookedBookings;
}
// =================================================== Reducers ==================================================== //

export function round(value: number, precision: number) {
	const multiplier = 10 ** precision;
	return Math.round(value * multiplier) / multiplier;
}

// ========================================== Table cell transformations =========================================== //

/**
 * Returns date time as "02 Feb, 13:45"
 */
export function transformTrip(booking: CheckInBookingOverviewDto | BookingEntity) {
	if (!booking.bookedSummary || !booking.bookedSummary.ferryTrip) {
		throw new Error('Booked summary ferry trip missing');
	}

	const { departureDateTime } = booking.bookedSummary.ferryTrip;
	const date = new Date(departureDateTime);

	return `${formatDateMonth(date)}, ${formatStandardTime(date)}`;
}

/**
 * Returns first letter of booking's assigned name (vehicle booking uses driver last name,
 * passenger booking uses user's last name).
 */
export function firstLetterLastName(booking: BookingEntity | CheckInBookingOverviewDto) {
	const bookingName = booking.getFullName(true) ?? '';
	return bookingName.charAt(0);
}

// ================================================ Other functions ================================================ //

export const CHECK_IN_ALERT_CONFIG: ToastOptions = {
	autoClose: 2200,
	pauseOnFocusLoss: false,
	pauseOnHover: false,
	position: 'top-left',
};

/**
 * Display notification for two actions.
 *
 * If nextStatus is true, message says "John Smith checked in".
 *
 * Otherwise, message says "John Smith undo check-in".
 *
 * Notification will last 2 seconds and appears top-left of screen.
 */
export function checkInAlert(
	booking: BookingEntity | CheckInBookingOverviewDto,
	newStatus: string,
	nextStatus: boolean,
) {
	alertToast(
		// Uses driver name, otherwise user name.
		booking.getFullName(true),
		nextStatus ? 'success' : undefined,
		newStatus,
		CHECK_IN_ALERT_CONFIG,
	);
	if (nextStatus) {
		PlayCheckInAudio();
	}
}

export function isAnyAddOnsToShow(booking: BookingEntity | CheckInBookingOverviewDto) {
	return booking.bookedSummary?.additionalBookingOptions?.some(x => x.option?.showInCheckIn ?? false);
}
export function getAddOnsToShow(booking: BookingEntity | CheckInBookingOverviewDto) {
	return booking.bookedSummary?.additionalBookingOptions?.filter(x => x.option?.showInCheckIn ?? false);
}

export function transformAddOns(
	booking: BookingEntity | CheckInBookingOverviewDto,
	addOns: AdditionalBookingOptionEntity[],
) {
	if (!booking.bookedSummary) {
		return null;
	}
	const values: string[] = [];
	const addOnsOnBooking = booking.bookedSummary.additionalBookingOptions;

	for (const addOn of addOns) {
		const value = addOnsOnBooking.find(x => x.option.id === addOn.id)?.quantity ?? 0;
		if (value && value !== 0) {
			values.push(value.toString());
		} else {
			// Use '-' as placeholder when value is undefined or 0
			values.push('-');
		}
	}
	return values.join(' | ');
}

export function addOnsHeader(addOns: AdditionalBookingOptionEntity[]) {
	const values: string[] = [];
	for (const addOn of addOns) {
		const value = stringNotEmpty(addOn.abbreviation) ? addOn.abbreviation : addOn.name;
		values.push(value);
	}
	return values.join(' | ');
}

// ============================================ Wrong disembark stop ============================================== //

/**
 * True when a forward status change advances a booking from a non-disembarked status into a
 * disembarked status while the stop being checked in at is not the booking's scheduled disembark
 * (end) stop. Used to warn staff before disembarking a booking at the wrong location.
 */
export function isDisembarkingAtWrongStop(
	currentStatus: CheckInStatusEntity | undefined | null,
	nextStatus: CheckInStatusEntity | undefined | null,
	isForwardStatusChange: boolean,
	currentStopId: string,
	endStopId: string,
): boolean {
	return (
		!(currentStatus?.disembarked ?? false) &&
		(nextStatus?.disembarked ?? false) &&
		isForwardStatusChange &&
		currentStopId !== endStopId
	);
}

/**
 * Shows the shared "Notice: Wrong Stop" confirmation for disembarking a booking at a stop that is
 * not its scheduled disembark stop. Resolves if the user chooses to continue and rejects if they
 * cancel — mirroring newConfirmModal's promise semantics.
 */
export function confirmDisembarkingAtWrongStop(endStopId: string): Promise<unknown> {
	const endStop = getStopById(endStopId);
	return newConfirmModal(
		'Notice: Wrong Stop',
		<p>
			This booking is scheduled to disembark at <strong>{endStop?.location.shortName}</strong>. Are you sure you
			want to continue?
		</p>,
		{
			confirmText: 'Yes, continue',
			cancelText: 'Back',
		},
	);
}
